import { router } from '@/router'
import { browserPathToRouter, redirectToLogin } from '@/utils/authLoginRedirect'
import { type ApiFetcherArgs, tsRestFetchApi } from '@ts-rest/core'

/**
 * A drop-in replacement for the default ts-rest fetcher that transparently
 * handles expired access tokens:
 *
 * 1. Makes the original request (with credentials so the httpOnly access
 *    cookie is attached).
 * 2. If the response is a 401 on an authenticated route, triggers a single
 *    `GET /refresh` call — concurrent 401s all await the same
 *    `refreshPromise` so we never spam the refresh endpoint.
 * 3. On refresh success, retries the original request and returns the retry
 *    response.
 * 4. On refresh failure, hard-redirects to the login page.
 *
 * On unauthenticated routes (login, forgot-password, email-sent,
 * confirm-forgot-password) a 401 is the expected response when no session
 * exists — we let it bubble up to the caller without attempting a refresh
 * or redirect.
 *
 * Passed into `initClient` in `useClient.ts`, this gives every query/mutation
 * in `src/queries/*` seamless session renewal without any per-call-site
 * knowledge of auth.
 */

let refreshPromise: Promise<Response> | null = null

export const authenticatedFetch = async (args: ApiFetcherArgs) => {
  const response = await tsRestFetchApi({
    ...args,
    credentials: 'include',
  })

  // Prefer the resolved router state, but on a cold load the router has not
  // yet completed its initial navigation (START_LOCATION), so fall back to
  // resolving the URL after stripping the app base.
  const currentRoute = router.currentRoute.value
  const resolved = currentRoute.matched.length > 0 ? currentRoute : router.resolve(browserPathToRouter())
  const isUnauthenticatedRoute = resolved.meta.unauthenticated === true

  if (response.status === 401 && !isUnauthenticatedRoute) {
    if (!refreshPromise) {
      refreshPromise = fetch(`${import.meta.env.VITE_API_BASE_URL}/refresh`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }).finally(() => {
        refreshPromise = null
      })
    }

    const refreshResponse = await refreshPromise

    if (refreshResponse.status === 200) {
      const retryResponse = await tsRestFetchApi({
        ...args,
        credentials: 'include',
      })
      return retryResponse
    }

    redirectToLogin()

    return response
  }

  return response
}
